/* AutoAssignPreviewRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
/* global Slick */

class AutoAssignPreviewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("auto-assign-summary-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._tableOptions = {
            editable: false,
            enableCellNavigation: false,
            enableAsyncPostRender: true,
            forceFitColumns: true,
            autoHeight: false,
            autoEdit: false,
            resizable: true,
            rowHeight: 40
        };

        // slick table functionality based off of AuditPolicyDetailsViewRenderer... minus any editable elements/listeners
        this._slickTableContainerAssaigned = document.createElement("columns-table");
        this._slickTableContainerUassaigned = document.createElement("columns-table");

        this._slickColumnDefs = [
            {
                id: "name",
                width: 100,
                name: "Device",
                field: "name",
                toolTip: "Device Name"
            },
            {
                id: "detectedHostName",
                width: 100,
                name: "Hostname",
                field: "detectedHostName",
                toolTip: "Hostname"
            },
            {
                id: "detectedVendor",
                width: 100,
                name: "Vendor",
                field: "detectedVendor",
                toolTip: "Vendor"
            },
            {
                id: "detectedSku",
                width: 100,
                name: "SKU",
                field: "detectedSku",
                toolTip: "SKU"
            },
            {
                id: "sitePath",
                width: 100,
                name: "Site Path",
                field: "sitePath",
                toolTip: "Site Path"
            }
        ];

        this._titlePanel = document.createElement("h2");
        this._titlePanel.classList.add("summary-title");
        this._summaryPanel = document.createElement("div");
        this._summaryPanel.classList.add("summary");
        this._emptyGridAssaigned = document.createElement("div");
        this._emptyGridAssaigned.classList.add("emptyMsg");
        this._emptyGridAssaigned.innerHTML = "No devices assigned";
        this._emptyGridUnassaigned = document.createElement("div");
        this._emptyGridUnassaigned.classList.add("emptyMsg", "hidden");
        this._emptyGridUnassaigned.innerHTML = "No devices un-assigned";
        this._emptyGridUnassaigned.classList.add("hidden");
        this._buttonBar = document.createElement("div");
        this._buttonBar.classList.add("summary-buttons");
        this._okButton = new ButtonControl({
            label: "Continue  ",
            darkTheme: true,
            type: "additive"
        });
        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true
        });

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._okButton.on("UserClick", (this._onOkClick = this._onOkClick.bind(this)));
        this._cancelButton.on("UserClick", (this._onCancelClick = this._onCancelClick.bind(this)));
    }
    _unregisterListeners() {
        this._okButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
    }

    _renderGrids() {
        this._slickDataViewAssaigned = new Slick.Data.DataView();
        this._slickDataViewUnassaigned = new Slick.Data.DataView();

        this._slickDataViewAssaigned.setItems(this._model._autoAssignResults.summary.devicesAssigned);
        this._slickDataViewUnassaigned.setItems(this._model._autoAssignResults.summary.devicesUnassigned);

        this._slickTableAssaigned = new Slick.Grid(
            this._slickTableContainerAssaigned,
            this._slickDataViewAssaigned,
            this._slickColumnDefs,
            this._tableOptions
        );

        this._slickTableUnsasaigned = new Slick.Grid(
            this._slickTableContainerUassaigned,
            this._slickDataViewUnassaigned,
            this._slickColumnDefs,
            this._tableOptions
        );
    }

    _refreshGrids() {
        //Calculate a good vale with a minimum that still allows 2 rows per grid
        let editorHeight = document.querySelectorAll("editor-layout-gl")[0].clientHeight;
        editorHeight = editorHeight < 540 ? 540 : editorHeight;
        const gridHeight = `calc( (${editorHeight}px * 0.50) - 15.5rem`;

        //set CSS values
        for (let el of document.querySelectorAll("columns-table")) {
            el.style.height = gridHeight;
        }
        for (let el of document.getElementsByClassName("emptyMsg")) {
            el.style["line-height"] = gridHeight;
        }

        //refresh grids
        this._slickTableAssaigned.resizeCanvas();
        this._slickTableUnsasaigned.resizeCanvas();
    }

    _refreshSlickGrid(slickDataView, slickTable) {
        slickDataView.refresh();
        slickTable.invalidateAllRows();
        slickTable.resizeCanvas();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /* jshint ignore:start */
    async shutdown() {
        this._unregisterListeners();
        await this._okButton.shutdown();
        await this._cancelButton.shutdown();
        this._summaryPanel = undefined;
        this._errorPanel = undefined;
    }
    /* jshint ignore:end */

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */

    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titlePanel);
                this._rootElem.appendChild(this._summaryPanel);
                this._rootElem.appendChild(this._buttonBar);
                this._buttonBar.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonBar.appendChild(this._okButton.renderer.rootElement);
                this._displaySummary();
            }
            resolve();
        }).then(() => {
            this._renderGrids();
            this._refreshGrids();
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onOkClick() {
        this._model.resolve();
    }

    _onCancelClick() {
        this._model.reject();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _setTitle() {
        this._titlePanel.innerHTML = "Preview of Changes";
    }

    _displaySummary() {
        this._setTitle();
        const { devicesAssigned, devicesUnassigned } = this._model._autoAssignResults.summary;

        let sitesAssignedLabel = document.createElement("h3");
        sitesAssignedLabel.innerText = `Devices To Be Auto-assigned (${devicesAssigned.length || 0})`;
        this._summaryPanel.appendChild(sitesAssignedLabel);
        if (devicesAssigned.length) {
            this._summaryPanel.appendChild(this._slickTableContainerAssaigned);
        } else {
            this._summaryPanel.appendChild(this._emptyGridAssaigned);
        }

        let sitesUnassignedLabel = document.createElement("h3");
        sitesUnassignedLabel.innerText = `Device No Longer Auto-assigned (${devicesUnassigned.length || 0})`;
        sitesUnassignedLabel.classList.add("hidden");
        this._summaryPanel.appendChild(sitesUnassignedLabel);
        if (devicesUnassigned.length) {
            this._slickTableContainerUassaigned.classList.add("hidden");
            this._summaryPanel.appendChild(this._slickTableContainerUassaigned);
        } else {
            this._emptyGridUnassaigned.classList.add("hidden");
            this._summaryPanel.appendChild(this._emptyGridUnassaigned);
        }
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = AutoAssignPreviewRenderer;
